import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { WalletItemCreditCard } from "../../../store/common";
import * as httpClient from "../../../util/httpClient";
import { Notification } from "../../common/Notification";
import { useForm } from "react-hook-form";
import { countryList } from "../../../util/CommonLists";
import { getFlowByName } from "../../../config/FlowName";
import * as NextStepFlowAction from "../../../store/nextStepFlowService/actions";
import { ApplicationState } from "../../../store";
import { PATTERNS } from "../../../util/patterns";
import { useTranslation } from "react-i18next";

const CreditDebitCardDialog: React.FunctionComponent<{ requestDataCallback(): void; walletItem: WalletItemCreditCard; isNew: boolean; closeDialog(): void }> = (
    props
) => {
    const { t: tLang, i18n: i18nLang } = useTranslation();
    const [isLoading, setisLoading] = useState(false);

    const dispatch = useDispatch();
    const lostWalletFlow = useSelector((state: ApplicationState) => getFlowByName(state, "LOST-WALLET-ASSISTANCE"));
    const completeGetStartedItem = (flowID: any, flowName: string) => dispatch(NextStepFlowAction.actionCreators.completeGetStartedItem(flowID, flowName));
    const [creditCard, setCreditCard] = useState(props.walletItem);
    const [expireMonth, setExpireMonth] = useState("");
    const [expireYear, setExpireYear] = useState("");
    const [errorMsg, seterrorMsg] = useState("");
    const [hasError, setHasError] = useState(false);

    /**Validation with react-hook-form ***/
    const { register, errors, formState, handleSubmit } = useForm({
        mode: "onBlur",
        reValidateMode: "onChange",
    });

    const { isSubmitting } = formState;

    useEffect(() => {
        if (props.walletItem && props.walletItem.expirationDate) {
            setExpireMonth(props.walletItem.expirationDate.trim().split("/")[0]);
            setExpireYear("20" + props.walletItem.expirationDate.trim().split("/")[1]);
        }
        if (props.isNew) {
            setCreditCard({ ...creditCard, cardType: "VISA" });
        } else {
            if (creditCard.cardCategory && creditCard.cardCategory.split(" ")[0]) {
                setCreditCard({ ...creditCard, cardCategory: creditCard.cardCategory.split(" ")[0].toUpperCase() });
            }
        }
    }, []);

    useEffect(() => {
        dateFormatter();
    }, [expireMonth, expireYear]);

    const closeDialog = () => {
        document.body.classList.remove("no_scroll");
        props.closeDialog();
    };

    const refresWalletItem = () => {
        closeDialog();
        props.requestDataCallback();
    };

    function dateFormatter() {
        setHasError(false);
        if (expireMonth && expireMonth != "" && expireYear && expireYear != "") {
            setCreditCard({ ...creditCard, expirationDate: expireMonth + "/" + expireYear });
        }
    }

    function onChangeHandler(event: any) {
        setHasError(false);
        const elName = event.currentTarget.name;
        setCreditCard({ ...creditCard, [elName as string]: event.currentTarget.value });
    }

    const saveCard = () => {
        const requestBody = {
            "lost-wallet": {
                "lost-wallet-creditcard": creditCard,
            },
        };
        setisLoading(true);
        httpClient
            .sendRequest("POST", "api/lostWallet/" + (props.isNew ? "addNewWalletItem" : "updateWalletItem"), requestBody)
            .then((data) => {
                setisLoading(false);
                if (data.isSuccess) {
                    if (lostWalletFlow && lostWalletFlow.flowStepStatus !== "COMPLETED") {
                        completeGetStartedItem(lostWalletFlow.flowStepId, lostWalletFlow.flowName ? lostWalletFlow.flowName : " ");
                    }
                    Notification({
                        type: "success",
                        title: props.isNew
                            ? tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.addToastTitle")
                            : tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.updateToastTitle"),
                        message: props.isNew
                            ? tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.addToastMessage", {
                                  cardType: creditCard.cardCategory ? creditCard.cardCategory : "Credit/Debit card",
                              })
                            : tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.updateToastMessage", {
                                  cardType: creditCard.cardCategory ? creditCard.cardCategory : "Credit/Debit card",
                              }),
                    });
                    refresWalletItem();
                } else if (data.hasError && data.errors.length > 0) {
                    seterrorMsg(data.errors.join(", "));
                    setHasError(true);
                } else {
                    seterrorMsg(tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.unknownError"));
                    setHasError(true);
                }
            })
            .catch(function (error) {
                setisLoading(false);
                Notification({
                    type: "error",
                    title: tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.errorToastTitle"),
                    message: tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.errorToastMesage"),
                    duration: 0,
                });
                closeDialog();
            });
    };

    return (
        <React.Fragment>
            {!isLoading ? (
                <>
                    <div className="dialog-header">
                        <div className="dialog-icon">
                            <em className={props.isNew ? "las la-plus" : "las la-edit"}></em>
                        </div>
                        <h1>
                            {props.isNew
                                ? tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.addLabel")
                                : tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.editLabel")}{" "}
                            {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.title")}
                        </h1>
                    </div>

                    <form onSubmit={handleSubmit(saveCard)} id="card-info-form-container">
                        <div className="dialog-body">
                            <div className="form-row inline">
                                <div className="row">
                                    <div className="col-xs-12 col-sm-4 col-no-gutter">
                                        <legend className="legend-label">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.cardCategory")}{" "}
                                            <span className="required" title="Mandatory">
                                                *
                                            </span>
                                        </legend>
                                    </div>

                                    <div className="col-xs-12 col-sm-8 col-no-gutter">
                                        <div className="row">
                                            <div className={errors["cardCategory"] ? "form-row row status-error" : "form-row row"}>
                                                <div className="col-xs-12 col-no-gutter">
                                                    <div className="form-row no-margin">
                                                        <div role="alert" className="form-input">
                                                            <label className="form-radio-label" htmlFor="edit-card-1-card-category-credit-card">
                                                                <input
                                                                    type="radio"
                                                                    id="edit-card-1-card-category-credit-card"
                                                                    aria-labelledby="edit-card-1-card-category-credit-card"
                                                                    name="cardCategory"
                                                                    value="CREDIT"
                                                                    defaultChecked={
                                                                        creditCard.cardCategory
                                                                            ? creditCard.cardCategory.split(" ")[0].toUpperCase() === "CREDIT"
                                                                            : false
                                                                    }
                                                                    onChange={onChangeHandler}
                                                                    ref={register({
                                                                        required: true,
                                                                    })}
                                                                />
                                                                <span className="form-radio-label-text">
                                                                    {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.creditRedio")}
                                                                </span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-xs-12 col-no-gutter">
                                                    <div className="form-row no-margin">
                                                        <div role="alert" className="form-input">
                                                            <label className="form-radio-label" htmlFor="edit-card-1-card-category-debit-card">
                                                                <input
                                                                    type="radio"
                                                                    id="edit-card-1-card-category-debit-card"
                                                                    aria-labelledby="edit-card-1-card-category-debit-card"
                                                                    name="cardCategory"
                                                                    value="DEBIT"
                                                                    defaultChecked={
                                                                        creditCard.cardCategory
                                                                            ? creditCard.cardCategory.split(" ")[0].toUpperCase() === "DEBIT"
                                                                            : false
                                                                    }
                                                                    onChange={onChangeHandler}
                                                                    ref={register({
                                                                        required: true,
                                                                    })}
                                                                />
                                                                <span className="form-radio-label-text">
                                                                    {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.debitRadio")}
                                                                </span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                {errors.cardCategory && errors.cardCategory.type === "required" && (
                                                    <span className="form-error">
                                                        {tLang(
                                                            "identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.validationMessage.cardCategory.required"
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={errors["cardHolderName"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                        <label htmlFor="edit-card-1-cardholder-name">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.cardholderName")}
                                        </label>
                                    </div>
                                    <div role="alert" className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                        <input
                                            aria-required="true"
                                            type="text"
                                            className="inputbox"
                                            id="edit-card-1-cardholder-name"
                                            aria-labelledby="edit-card-1-cardholder-name"
                                            name="cardHolderName"
                                            placeholder={tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.placeholder.cardholderName")}
                                            defaultValue={creditCard.cardHolderName}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                pattern: PATTERNS.CARDHOLDERNAME.pattern,
                                            })}
                                            maxLength={30}
                                        />
                                        {errors.cardHolderName && errors.cardHolderName.type === "pattern" && (
                                            <span className="form-error">
                                                {tLang(
                                                    "identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.validationMessage.cardholderName.pattern"
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={errors["cardType"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                        <label htmlFor="edit-card-1-card-type">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.cardType")}{" "}
                                            <span className="required" title="Mandatory">
                                                *
                                            </span>
                                        </label>
                                    </div>
                                    <div role="alert" className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                        <select
                                            aria-required="true"
                                            id="edit-card-1-card-type"
                                            aria-labelledby="edit-card-1-card-type"
                                            className="inputbox"
                                            name="cardType"
                                            defaultValue={creditCard.cardType}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                required: true,
                                            })}
                                        >
                                            <option value="VISA">Visa</option>
                                            <option value="MASTERCARD">Mastercard</option>
                                            <option value="AMERICAN EXPRESS">American Express</option>
                                            <option value="DISCOVER">Discover</option>
                                        </select>
                                        {errors.cardType && errors.cardType.type === "required" && (
                                            <span className="form-error">
                                                {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.validationMessage.cardType.required")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={errors["cardNumber"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                        <label htmlFor="edit-card-1-card-number">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.cardNumber")}{" "}
                                            <span className="required" title="Mandatory">
                                                *
                                            </span>
                                        </label>
                                    </div>
                                    <div role="alert" className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                        <input
                                            aria-required="true"
                                            type="text"
                                            className="inputbox"
                                            id="edit-card-1-card-number"
                                            aria-labelledby="edit-card-1-card-number"
                                            name="cardNumber"
                                            defaultValue={creditCard.cardNumber}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                required: true,
                                                pattern: creditCard.cardType === "AMERICAN EXPRESS" ? /^$|^(\d{15})$/ : /^$|^(\d{16})$/,
                                            })}
                                            maxLength={creditCard.cardType === "AMERICAN EXPRESS" ? 15 : 16}
                                        />
                                        {errors.cardNumber && errors.cardNumber.type === "required" && (
                                            <span className="form-error">
                                                {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.validationMessage.cardNumber.required")}
                                            </span>
                                        )}

                                        {errors.cardNumber && errors.cardNumber.type === "pattern" && (
                                            <span className="form-error">
                                                {tLang(
                                                    "identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.validationMessage.cardNumber.patternInit"
                                                )}{" "}
                                                <span>
                                                    {creditCard.cardType === "AMERICAN EXPRESS" ? (
                                                        <strong>
                                                            {tLang(
                                                                "identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.validationMessage.cardNumber.patternMid15"
                                                            )}
                                                        </strong>
                                                    ) : (
                                                        <strong>
                                                            {tLang(
                                                                "identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.validationMessage.cardNumber.patternMid16"
                                                            )}
                                                        </strong>
                                                    )}
                                                </span>{" "}
                                                {tLang(
                                                    "identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.validationMessage.cardNumber.patternEnd"
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={errors["cardName"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                        <label htmlFor="edit-card-1-card-name">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.card-bankName")}{" "}
                                            <span className="required" title="Mandatory">
                                                *
                                            </span>
                                        </label>
                                    </div>
                                    <div role="alert" className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                        <input
                                            aria-required="true"
                                            type="text"
                                            className="inputbox"
                                            id="edit-card-1-card-name"
                                            aria-labelledby="edit-card-1-card-name"
                                            name="cardName"
                                            defaultValue={creditCard.cardName}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                required: true,
                                            })}
                                            maxLength={50}
                                        />
                                        {errors.cardName && errors.cardName.type === "required" && (
                                            <span className="form-error">
                                                {tLang(
                                                    "identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.validationMessage.card-bankName.required"
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div role="group" className="form-row inline">
                                <div className="row">
                                    <div className="col-xs-12 col-sm-4 col-no-gutter">
                                        <legend className="legend-label"> <label htmlFor="edit-card-1-expiry-month">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.expiryDate")}
                                        </label></legend>
                                    </div>

                                    <div className="col-xs-12 col-sm-8 col-no-gutter">
                                        <div className="row">
                                            <div className="col-xs-4 col-no-gutter">
                                                <div className="form-row row no-margin">
                                                    <div className="form-label col-xs-12 col-no-gutter sr-only">
                                                        <label htmlFor="edit-card-1-expiry-month-1">Expiry Month</label>
                                                    </div>
                                                    <div className="form-input col-xs-12 col-no-gutter  padding-right-xs">
                                                        <select
                                                            id="edit-card-1-expiry-month"
                                                            className="inputbox"
                                                            value={expireMonth}
                                                            onChange={(e) => setExpireMonth(e.currentTarget.value)}
                                                        >
                                                            <option value="" disabled>
                                                                {tLang(
                                                                    "identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.dropdownStaticValue.expiryDateMonth"
                                                                )}
                                                            </option>
                                                            <option value="01">01</option>
                                                            <option value="02">02</option>
                                                            <option value="03">03</option>
                                                            <option value="04">04</option>
                                                            <option value="05">05</option>
                                                            <option value="06">06</option>
                                                            <option value="07">07</option>
                                                            <option value="08">08</option>
                                                            <option value="09">09</option>
                                                            <option value="10">10</option>
                                                            <option value="11">11</option>
                                                            <option value="12">12</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-xs-4 col-no-gutter">
                                                <div className="form-row row no-margin">
                                                    <div className="form-label col-xs-12 col-no-gutter sr-only">
                                                        <label htmlFor="edit-card-1-expiry-year">Expiry Year</label>
                                                    </div>
                                                    <div className="form-input col-xs-12 col-no-gutter">
                                                        <select
                                                            id="edit-card-1-expiry-year"
                                                            className="inputbox"
                                                            value={expireYear}
                                                            onChange={(e) => setExpireYear(e.currentTarget.value)}
                                                        >
                                                            <option value="" disabled>
                                                                {tLang(
                                                                    "identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.dropdownStaticValue.expiryDateYear"
                                                                )}
                                                            </option>
                                                            {Array(10)
                                                                .fill(null)
                                                                .map((value, index) => {
                                                                    const optionValue = new Date().getFullYear() + index;
                                                                    return (
                                                                        <option key={optionValue} value={optionValue}>
                                                                            {optionValue}
                                                                        </option>
                                                                    );
                                                                })}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={errors["pin"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                        <label htmlFor="edit-card-1-pin">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.pin")}
                                        </label>
                                    </div>
                                    <div role="alert" className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                        <input
                                            aria-required="true"
                                            type="text"
                                            className="inputbox"
                                            id="edit-card-1-pin"
                                            aria-labelledby="edit-card-1-pin"
                                            name="pin"
                                            defaultValue={creditCard.pin}
                                            maxLength={4}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                pattern: /^$|^(\d{4})$/,
                                            })}
                                        />
                                        {errors.pin && errors.pin.type === "pattern" && (
                                            <span className="form-error">
                                                {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.validationMessage.pin.pattern")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="form-row inline row">
                                <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                    <label htmlFor="edit-card-1-card-level">
                                        {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.cardLevel")}
                                    </label>
                                </div>
                                <div className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                    <input
                                        type="text"
                                        className="inputbox"
                                        id="edit-card-1-card-level"
                                        aria-labelledby="edit-card-1-card-level"
                                        name="cardLevel"
                                        maxLength={30}
                                        defaultValue={creditCard.cardLevel}
                                        onChange={onChangeHandler}
                                    />
                                </div>
                            </div>

                            <div className="form-row inline row">
                                <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                    <label htmlFor="edit-card-1-issuing-country">
                                        {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.country")}
                                    </label>
                                </div>
                                <div className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                    <select
                                        className="inputbox"
                                        id="edit-card-1-issuing-country"
                                        role="listbox"
                                        defaultValue={creditCard.issuingCountry}
                                        name="issuingCountry"
                                        onChange={onChangeHandler}
                                    >
                                        <option value="">{tLang("identityVault.secureStorage.addWalletDropDownList.creditDebitPlaceHolder.selectCountry")}</option>
                                        {countryList.map((val: any) => (
                                            <option key={val.code} value={val.code}>
                                                {val.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row inline row">
                                <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                    <label htmlFor="edit-card-1-wesbite">
                                        {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.website")}
                                    </label>
                                </div>
                                <div className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                    <input
                                        type="text"
                                        className="inputbox"
                                        id="edit-card-1-wesbite"
                                        aria-labelledby="edit-card-1-wesbite"
                                        maxLength={50}
                                        name="webSite"
                                        defaultValue={creditCard.webSite}
                                        onChange={onChangeHandler}
                                    />
                                </div>
                            </div>

                            <div className={errors["customerServicePhoneNumber"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                        <label htmlFor="edit-card-1-customer-service-phone-number">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.serviceNumber")}
                                        </label>
                                    </div>
                                    <div role="alert" className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                        <input
                                            aria-required="true"
                                            type="text"
                                            className="inputbox"
                                            id="edit-card-1-customer-service-phone-number"
                                            aria-labelledby="edit-card-1-customer-service-phone-number"
                                            maxLength={10}
                                            name="customerServicePhoneNumber"
                                            defaultValue={creditCard.customerServicePhoneNumber}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                pattern: /^$|^[(]?\d{3}[)]?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                                            })}
                                        />
                                        {errors.customerServicePhoneNumber && errors.customerServicePhoneNumber.type === "pattern" && (
                                            <span className="form-error">
                                                {tLang(
                                                    "identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.validationMessage.customerServiceNumber.pattern"
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="form-row inline row">
                                <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                    <label htmlFor="edit-card-1-notes">
                                        {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.notes")}
                                    </label>
                                </div>
                                <div className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                    <textarea
                                        className="inputbox"
                                        id="edit-card-1-notes"
                                        aria-labelledby="edit-card-1-notes"
                                        name="notes"
                                        maxLength={500}
                                        style={{ resize: "none" }}
                                        placeholder=""
                                        rows={6}
                                        defaultValue={creditCard.notes}
                                        onChange={onChangeHandler}
                                    ></textarea>
                                </div>
                            </div>
                            {hasError && (
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter"></div>
                                    <div className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                        <p className="form-error">{errorMsg}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="dialog-footer">
                            <div className="row between-xs">
                                <div className="col-xs col-no-gutter flex-grow-null">
                                    <button type="button" className="btn-default btn-link btn-no-padding btn-no-border" onClick={() => closeDialog()}>
                                        <span className="btn-text">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.cancelButtonText")}
                                        </span>
                                    </button>
                                </div>
                                <div className="col-xs col-no-gutter flex-grow-null">
                                    <button type="submit" className="btn-default btn-primary" disabled={isSubmitting}>
                                        <span className="btn-text">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.creditDebitCardPopup.label.saveButtonText")}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </>
            ) : (
                <div className="box loading"></div>
            )}
        </React.Fragment>
    );
};

export default CreditDebitCardDialog;
